import { formatRecordedMoney } from "utils/recordedMoney";
import AccountPage from "../AccountPage";
import AccountLayout from "../AccountLayout";
import useProfile from "api/UserResource/useProfile";
import useGetAssignedOrders from "api/OrderResource/useGetAssignedOrders";
import { DataTable } from "primereact/datatable";
import { getPaymentMethodLabel } from "utils";
import { Tag } from "primereact/tag";
import { Column } from "primereact/column";
import useAssignedOrdersFilters from "./useAssignedOrdersFilters";
import SearchInput from "./SearchInput";
import RowsPerPageSelector from "./RowsPerPageSelector";
import ClearFiltersButton from "./ClearFiltersButton";
import { CustomPaginator } from "components";
import MoreOrderDetails from "./MoreOrderDetails";

// Table cell templates
const ReferenceCell = ({ ecocash_reference, reference }) => ecocash_reference || reference || "N/A";

const PaymentMethodCell = ({ payment_method, phone }) => {
	if (!payment_method?.startsWith("Ecocash")) {
		return <span>{payment_method}</span>;
	}

	return (
		<div className="flex flex-col">
			<span>{getPaymentMethodLabel(payment_method)}</span>
			{phone && <span className="text-xs font-bold">{phone}</span>}
		</div>
	);
};

const AmountCell = ({ total_amount, currency, currency_details }) => (
	<div className="font-bold">{formatRecordedMoney(total_amount, currency, currency_details)}</div>
);

const StatusCell = ({ status = "Pending" }) => (
	<Tag
		rounded
		className="font-sans text-xs"
		severity={
			["completed", "delivered"].includes(status.toLowerCase())
				? "success"
				: ["cancelled", "failed"].includes(status.toLowerCase())
					? "danger"
					: "info"
		}
		value={status}
	/>
);

// Table header component
const TableHeader = ({ totalRecords, filters, onSearch, onClear, onRowsPerPageChange }) => {
	const hasActiveFilters = filters.search || filters.page > 1 || filters.rowsPerPage !== 10;

	return (
		<div className="space-y-4">
			<p className="account-helper">
				{totalRecords} {totalRecords === 1 ? "order" : "orders"}
			</p>

			<div className="account-orders-toolbar">
				<div className="account-orders-search-group">
					<SearchInput
						onSearch={onSearch}
						onClear={onClear}
						currentSearch={filters.search}
						placeholder="Search order number, reference, etc..."
					/>
					<ClearFiltersButton onClear={onClear} hasActiveFilters={hasActiveFilters} />
				</div>
				<RowsPerPageSelector value={filters.rowsPerPage} onChange={onRowsPerPageChange} />
			</div>
		</div>
	);
};

const MyAssignedOrders = () => {
	const { data: profile, isLoading: isLoadingProfile } = useProfile();
	const { filters, updateFilters, clearSearch } = useAssignedOrdersFilters();

	const {
		data,
		isLoading: isLoadingOrders,
		isError: ordersError,
		refetch,
	} = useGetAssignedOrders({
		queryParams: {
			page: filters.page,
			rows_per_page: filters.rowsPerPage,
			search: filters.search,
		},
	});

	const orders = data?.records;
	const totalRecords = data?.total_records || 0;
	const isLoading = isLoadingProfile || isLoadingOrders;

	const handleSearch = (searchTerm) => {
		updateFilters({ search: searchTerm, page: 1 });
	};

	const handleRowsPerPageChange = (rowsPerPage) => {
		updateFilters({ rowsPerPage, page: 1 });
	};

	const handlePageChange = (page) => {
		updateFilters({ page });
	};

	const handleFirstPageChange = (firstPage) => {
		const page = Math.floor(firstPage / filters.rowsPerPage) + 1;
		updateFilters({ page });
	};

	return (
		<AccountPage>
			<AccountLayout isLoading={isLoading} profile={profile?.data}>
				<div className="space-y-4">
					<DataTable
						loading={isLoadingOrders}
						value={orders}
						dataKey="id"
						tableStyle={{ minWidth: "44rem" }}
						emptyMessage={
							ordersError ? (
								<div className="account-empty" role="alert">
									<h2>Orders couldn’t be loaded</h2>
									<button type="button" className="account-button" onClick={() => refetch()}>
										Try again
									</button>
								</div>
							) : (
								<div className="account-empty">
									<h2>{filters.search ? "No matching orders" : "No orders yet"}</h2>
									<p>
										{filters.search
											? "Try another order number or clear your search."
											: "Your purchases will appear here."}
									</p>
								</div>
							)
						}
						header={
							<TableHeader
								totalRecords={totalRecords}
								filters={filters}
								onSearch={handleSearch}
								onClear={clearSearch}
								onRowsPerPageChange={handleRowsPerPageChange}
							/>
						}
						tableClassName="font-sans text-sm w-full"
						autoLayout
					>
						<Column
							body={(row) => (
								<MoreOrderDetails
									order_number={row.order_number}
                                    currency={row.currency} total_amount={row.total_amount} currency_details={row.currency_details}
									books={row.books}
									paymentType={row.payment_type}
									subscription={row.subscription}
									reference={row.reference || row.ecocash_reference || "N/A"}
									status={row.status}
									paymentMethod={row.payment_method}
									shippingAddress={row.shipping_address}
									created_at={row.created_at}
								/>
							)}
							header="Order Details"
						/>
						<Column body={ReferenceCell} header="Reference" className="hidden sm:table-cell" />
						<Column
							header="Payment method"
							body={PaymentMethodCell}
							className="hidden lg:table-cell"
						/>
						<Column header="Status" body={StatusCell} className="hidden md:table-cell" />
						<Column field="created_at" header="Transaction Date" className="hidden md:table-cell" />

						<Column header="Amount" body={AmountCell} className="w-36 text-right" />
					</DataTable>

					{totalRecords > 0 && (
						<div className="flex flex-wrap gap-4 justify-between items-center">
							<div className="text-sm text-gray-600">
								Showing {(filters.page - 1) * filters.rowsPerPage + 1} to{" "}
								{Math.min(filters.page * filters.rowsPerPage, totalRecords)} of {totalRecords}{" "}
								orders
							</div>
							<CustomPaginator
								setPage={handlePageChange}
								firstPage={(filters.page - 1) * filters.rowsPerPage}
								rowsPerPage={filters.rowsPerPage}
								setFirstPage={handleFirstPageChange}
								setRowsPerPage={handleRowsPerPageChange}
								totalRecords={totalRecords}
								showRowsPerPageDropdown={false}
							/>
						</div>
					)}
				</div>
			</AccountLayout>
		</AccountPage>
	);
};

export default MyAssignedOrders;
